/**
 * Tutor row for the management {@link DataTable}.
 * Column set changes with the active tab; shared cells live above the tab views.
 */
import * as React from 'react';
import { User, Mail, Phone, CalendarDays, Users } from 'lucide-react';
import { Button } from '@/components/forms/Button';
import { LabelBadge } from '@/components/common/LabelBadge';
import { TableRow } from '@/components/common/data-table';
import type { TutorInPersonTabView } from '@/components/tutors/tutor-configs';
import { useDepartments } from '@/hooks/useDepartments';
import type { TutorRecord } from '@/types/inperson/tutor.types';
import {
  deriveTutorRowDisplay,
  deriveTutorRowActionFlags,
  type TutorPageActions,
  type TutorRowDisplay,
} from './tutor-table-utils';

/** Props for {@link TutorRow}. */
interface TutorRowProps {
  record: TutorRecord;
  tabView?: TutorInPersonTabView;
  onView?: (id: string) => void;
  onEdit?: (id: string) => void;
  onDelete?: (id: string) => void;
  onManageAssignment?: (record: TutorRecord) => void;
  pageActions?: TutorPageActions;
}

/** Shared props for tab-specific table row layouts. */
type RowViewProps = Readonly<{
  record: TutorRecord;
  display: TutorRowDisplay;
  actions: ReturnType<typeof deriveTutorRowActionFlags>;
  onView?: (id: string) => void;
  onEdit?: (id: string) => void;
  onDelete?: (id: string) => void;
  onManageAssignment?: (record: TutorRecord) => void;
}>;

/** Name + avatar cell (shared across tabs). */
function NameCell({ display }: Readonly<{ display: TutorRowDisplay }>) {
  return (
    <td>
      <div className="flex items-center gap-2">
        <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-[color-mix(in_srgb,var(--color-1-nuance-1)_10%,transparent)]">
          <User className="w-4 h-4 text-[var(--color-1-nuance-1)]" />
        </div>
        <div className="flex flex-col">
          <span className="font-medium text-foreground">{display.displayName}</span>
        </div>
      </div>
    </td>
  );
}

/** View / edit / delete / assignment action buttons. */
function ActionsCell({
  record,
  actions,
  onView,
  onEdit,
  onDelete,
  onManageAssignment,
}: Readonly<{
  record: TutorRecord;
  actions: ReturnType<typeof deriveTutorRowActionFlags>;
  onView?: (id: string) => void;
  onEdit?: (id: string) => void;
  onDelete?: (id: string) => void;
  onManageAssignment?: (record: TutorRecord) => void;
}>) {
  return (
    <td className="text-center">
      <div className="flex items-center justify-center gap-1">
        <Button preset="row-view" onClick={() => onView?.(record.userId)} />
        {actions.canEdit && <Button preset="row-edit" onClick={() => onEdit?.(record.userId)} />}
        {actions.canDelete && (
          <Button preset="row-delete" onClick={() => onDelete?.(record.userId)} />
        )}
        {actions.assignmentButtonPreset && (
          <Button
            preset={actions.assignmentButtonPreset}
            disabled={actions.isAssignmentButtonDisabled}
            onClick={() => onManageAssignment?.(record)}
          />
        )}
      </div>
    </td>
  );
}

/** Department labels (muted when empty). */
function DepartmentsCell({ display }: Readonly<{ display: TutorRowDisplay }>) {
  return (
    <td>
      <span className={display.hasDepartments ? 'text-sm' : 'text-sm text-muted-foreground'}>
        {display.departmentsLabel}
      </span>
    </td>
  );
}

/** Actor name or an em dash. */
function ActorCell({ name }: Readonly<{ name: string | null }>) {
  if (name)
    return (
      <td>
        <span className="text-sm">{name}</span>
      </td>
    );
  return (
    <td>
      <span className="text-sm text-muted-foreground">—</span>
    </td>
  );
}

/** Columns for the active-tutors tab. */
function TutorsView({ record, display, actions, ...callbacks }: RowViewProps) {
  const { contact } = display;
  return (
    <TableRow>
      <NameCell display={display} />
      <td>
        <div className="flex flex-col gap-0.5">
          <span className="flex items-center gap-1 text-sm">
            <contact.primary.icon className="w-3.5 h-3.5 text-[var(--color-1-nuance-1)] shrink-0" />
            {contact.primary.value}
          </span>
          {contact.secondaryValue && (
            <span className="text-xs text-muted-foreground">{contact.secondaryValue}</span>
          )}
        </div>
      </td>
      <DepartmentsCell display={display} />
      <td>
        <LabelBadge
          label={display.situationConfig.label}
          icon={display.SituationIcon}
          variant={display.situationConfig.badgeVariant}
          color={display.situationConfig.badgeColor}
          size="sm"
        />
      </td>
      <td>
        <div className="flex items-center gap-1.5">
          <CalendarDays className="w-3.5 h-3.5 text-[var(--color-1-nuance-1)] shrink-0" />
          <span className="text-sm whitespace-nowrap">{display.createdAtLabel}</span>
        </div>
      </td>
      <td>
        <span className="text-sm whitespace-nowrap">{display.seniorityLabel}</span>
      </td>
      <td>
        <span className="text-sm text-muted-foreground">—</span>
      </td>
      <td>
        <div className="flex items-center gap-1">
          <Users className="w-3.5 h-3.5 text-[var(--color-1-nuance-1)] shrink-0" />
          <span className="text-sm">—</span>
        </div>
      </td>
      <ActionsCell record={record} actions={actions} {...callbacks} />
    </TableRow>
  );
}

/** Columns for the pre-registered tab (progress + milestones). */
function PreRegisteredView({ record, display, actions, ...callbacks }: RowViewProps) {
  return (
    <TableRow>
      <NameCell display={display} />
      <td>
        <div className="flex flex-col gap-0.5">
          {display.hasEmail && (
            <span className="text-xs flex items-center gap-1">
              <Mail className="w-3 h-3 text-[var(--color-1-nuance-1)]" />
              {display.emailLabel}
            </span>
          )}
          {display.hasPhone && (
            <span className="text-xs flex items-center gap-1">
              <Phone className="w-3 h-3 text-[var(--color-1-nuance-1)]" />
              {display.phoneLabel}
            </span>
          )}
        </div>
      </td>
      <td>
        <LabelBadge
          label={display.situationConfig.label}
          icon={display.SituationIcon}
          variant={display.situationConfig.badgeVariant}
          color={display.situationConfig.badgeColor}
          size="sm"
        />
      </td>
      <td>
        <div className="flex items-center gap-2">
          <div className="w-20 h-1.5 bg-[var(--color-1-nuance-3)] rounded-full overflow-hidden">
            <div
              className="h-full bg-[var(--color-2-nuance-1)] rounded-full transition-all"
              style={{ width: `${display.completionPct}%` }}
            />
          </div>
          <span className="text-xs text-muted-foreground tabular-nums">
            {display.completionPctLabel}
          </span>
        </div>
      </td>
      <td className="!px-3">
        <div className="flex items-center gap-1.5">
          {display.milestones.map((m) => {
            const Icon = m.icon;
            return (
              <div key={m.key} className="flex flex-col items-center gap-0.5" title={m.label}>
                <Icon className="w-4 h-4" style={{ color: m.color }} />
                <span className="text-[10px]" style={{ color: m.color }}>
                  {m.label}
                </span>
              </div>
            );
          })}
        </div>
      </td>
      <DepartmentsCell display={display} />
      <ActionsCell record={record} actions={actions} {...callbacks} />
    </TableRow>
  );
}

/** Columns for the to-validate tab (recruiter / coordinator). */
function ToValidateView({ record, display, actions, ...callbacks }: RowViewProps) {
  return (
    <TableRow>
      <NameCell display={display} />
      <DepartmentsCell display={display} />
      <ActorCell name={display.proposedByName} />
      <ActorCell name={display.validatedByName} />
      <ActionsCell record={record} actions={actions} {...callbacks} />
    </TableRow>
  );
}

/** Columns for the to-reassign tab (refusal reason). */
function ToReassignView({ record, display, actions, ...callbacks }: RowViewProps) {
  return (
    <TableRow>
      <NameCell display={display} />
      <DepartmentsCell display={display} />
      <ActorCell name={display.proposedByName} />
      <ActorCell name={display.refusedByName} />
      <td>
        <span className="text-sm text-muted-foreground">{display.refusalReasonLabel}</span>
      </td>
      <ActionsCell record={record} actions={actions} {...callbacks} />
    </TableRow>
  );
}

/** Default / "all" tab columns. */
function AllView({ record, display, actions, ...callbacks }: RowViewProps) {
  return (
    <TableRow>
      <NameCell display={display} />
      <td>
        <div className="flex items-center gap-2">
          <Mail className="w-4 h-4" style={{ color: 'var(--color-1-nuance-1)' }} />
          <span className="text-sm">{display.emailLabel}</span>
        </div>
      </td>
      <td>
        <div className="flex items-center gap-2">
          <Phone className="w-4 h-4" style={{ color: 'var(--color-1-nuance-1)' }} />
          <span className="text-sm">{display.phoneLabel}</span>
        </div>
      </td>
      <DepartmentsCell display={display} />
      <td>
        <LabelBadge
          label={display.situationConfig.label}
          icon={display.SituationIcon}
          variant={display.situationConfig.badgeVariant}
          color={display.situationConfig.badgeColor}
          size="md"
        />
      </td>
      <td>
        <div className="flex items-center gap-1.5">
          <CalendarDays className="w-3.5 h-3.5 text-[var(--color-1-nuance-1)] shrink-0" />
          <span className="text-sm whitespace-nowrap">{display.createdAtLabel}</span>
        </div>
      </td>
      <ActionsCell record={record} actions={actions} {...callbacks} />
    </TableRow>
  );
}

/** Tutor row for the management {@link DataTable}. */
export const TutorRow: React.FC<TutorRowProps> = ({
  record,
  tabView = 'tutors',
  onView,
  onEdit,
  onDelete,
  onManageAssignment,
  pageActions,
}) => {
  const { data: departments = [] } = useDepartments('FR');
  /** Flattened labels/flags for this row. */
  const display = deriveTutorRowDisplay(record, departments);
  /** Edit / delete / assignment CTA flags from RBAC + current tab. */
  const actions = deriveTutorRowActionFlags(record, tabView, pageActions);
  /** Bundle passed to the tab-specific row layout. */
  const viewProps: RowViewProps = {
    record,
    display,
    actions,
    onView,
    onEdit,
    onDelete,
    onManageAssignment,
  };

  if (tabView === 'tutors') return <TutorsView {...viewProps} />;
  if (tabView === 'pre_registered') return <PreRegisteredView {...viewProps} />;
  if (tabView === 'to_validate') return <ToValidateView {...viewProps} />;
  if (tabView === 'to_reassign') return <ToReassignView {...viewProps} />;
  return <AllView {...viewProps} />;
};
